import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail } from "lucide-react";
import { SITE_LOGO } from "../data/content";

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-primary text-white">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white p-1.5">
                <img
                  src={SITE_LOGO}
                  alt="Travel Club Navigator Group logo — globe with airplane and arrow"
                  className="h-full w-full object-contain"
                />
              </span>
              <span className="font-display text-lg font-extrabold text-white">
                Travel Club Navigator
              </span>
            </Link>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/85">
              Your transparent, zero-stress guide to travel clubs, timeshares, and the
              world's most exclusive escapes.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li>
                <a
                  href="mailto:hello@travelclubnavigator.co"
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-white/90 transition-colors duration-200 hover:text-white"
                >
                  <Mail className="h-4 w-4 flex-none text-white" />
                  hello@travelclubnavigator.co
                </a>
              </li>
            </ul>
            <div className="mt-6 flex gap-3">
              {[
                { Icon: Instagram, label: "instagram" },
                { Icon: Facebook, label: "facebook" },
                { Icon: Youtube, label: "youtube" },
                { Icon: Mail, label: "email" },
              ].map(({ Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  data-testid={`footer-social-${label}`}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-white transition-colors duration-200 hover:bg-white hover:text-primary"
                  aria-label={`${label} link`}
                >
                  <Icon className="h-4.5 w-4.5" />
                </a>
              ))}
            </div>
          </div>

          <FooterCol
            title="Reviews"
            links={[
              { label: "Timeshare Reviews", to: "/timeshare-reviews" },
              { label: "Presentation Guide", to: "/timeshare-reviews/presentation-guide" },
              { label: "Club Travalia", to: "/travel-club-reviews/club-travalia" },
              { label: "Ascendant Holidays", to: "/travel-club-reviews/ascendant-holidays" },
              { label: "Aloha Travelers", to: "/travel-club-reviews/aloha-travelers" },
              { label: "Arrivia", to: "/travel-club-reviews/arrivia" },
            ]}
          />
          <FooterCol
            title="Learn"
            links={[
              { label: "New Member Guide", to: "/members" },
              { label: "Maximize Your Points", to: "/guides/maximize-your-points" },
              { label: "Avoid Rising Fees", to: "/guides/avoid-rising-maintenance-fees" },
              { label: "All Guides", to: "/guides" },
              { label: "Travel Blog", to: "/blog" },
            ]}
          />
          <FooterCol
            title="Explore"
            links={[
              { label: "Vacation Packages", to: "/packages" },
              { label: "Partner With Us", to: "/partners" },
              { label: "Contact Us", to: "/contact" },
              { label: "Home", to: "/" },
            ]}
          />
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/20 pt-8 text-sm text-white/70 md:flex-row md:items-center">
          <p>© {new Date().getFullYear()} Travel Club Navigator Group. All rights reserved.</p>
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" aria-label="Legal">
            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-link"
              className="text-white/85 transition-colors duration-200 hover:text-white hover:underline"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              data-testid="footer-terms-link"
              className="text-white/85 transition-colors duration-200 hover:text-white hover:underline"
            >
              Terms of Service
            </Link>
            <Link
              to="/accessibility"
              data-testid="footer-accessibility-link"
              className="text-white/85 transition-colors duration-200 hover:text-white hover:underline"
            >
              Accessibility
            </Link>
          </nav>
        </div>
        <p className="mt-6 font-serif-accent italic text-white/70">
          Navigating your travel journey with transparency and zero stress.
        </p>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="font-display text-sm font-bold uppercase tracking-[0.16em] text-white">
      {title}
    </h4>
    <ul className="mt-5 space-y-3">
      {links.map((l) => (
        <li key={l.to + l.label}>
          <Link
            to={l.to}
            className="text-sm text-white/90 transition-colors duration-200 hover:text-white hover:underline"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);
